import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Slider } from "@/components/ui/slider";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { fr, es } from "date-fns/locale";
import type { DateRange } from "react-day-picker";
import {
  ArrowLeft, ArrowRight, LayoutGrid, CalendarDays, Users, MapPin, Sparkles, Lock,
  Building2, Sun, PartyPopper, Mic, Rocket, Compass, Euro, Mail, CalendarIcon, X, CheckCircle, Star,
  UserRound, UsersRound, Building, Landmark, Minus, Plus } from
"lucide-react";
import { cn } from "@/lib/utils";
import { useArkemaLang } from "@/components/thales/i18n";
import { sendSlackMessage } from "@/lib/sendSlackMessage";
import { getStoredUTMParams, getInitialUrl } from "@/lib/analytics";
import { getCampaignName } from "@/lib/campaignMapping";
import posthog from "posthog-js";
import confetti from "canvas-confetti";
import { getCeliaMeetingEmbedUrl } from "@/components/thales/celiaMeeting";
import { trackPostHogEvent } from "@/lib/analytics";

interface QuizModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEPS = [
{ label: "Type", icon: LayoutGrid },
{ label: "Dates", icon: CalendarDays },
{ label: "Catégories", icon: Compass },
{ label: "Participants", icon: Users },
{ label: "Standing", icon: Star },
{ label: "Votre match", icon: MapPin }];

const STEP_TITLES = [
"Quel type d'événement organisez-vous ?",
"Quand souhaitez-vous organiser votre événement ?",
"Quel type de destination recherchez-vous ?",
"Quelle est la taille de votre groupe ?",
"Quel standing souhaitez-vous ?",
"",
];

const STEP_LABELS = [
"Étape 1/6 — C'est parti !",
"Étape 2/6 — Vous êtes sur la bonne voie !",
"Étape 3/6 — On avance bien !",
"Étape 4/6 — Plus que 2 étapes !",
"Étape 5/6 — On y est presque !",
"Étape 6/6 — Dernière ligne droite !"];


const COMPATIBILITY_PERCENTS = [17, 34, 51, 68, 85, 100];

const EVENT_TYPES = [
{ value: "seminaire", label: "Séminaire d'entreprise", icon: Building2 },
{ value: "journee_hors_site", label: "Journée hors site", icon: Sun },
{ value: "soiree", label: "Soirée d'entreprise", icon: PartyPopper },
{ value: "team_building", label: "Activité team building", icon: Users },
{ value: "conference", label: "Conférence", icon: Mic },
{ value: "lancement_produit", label: "Lancement de produit", icon: Rocket }];


const MONTHS = [
"Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
"Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];


const CATEGORIES = [
{ emoji: "🏖️", label: "Plage" },
{ emoji: "🏔️", label: "Montagne" },
{ emoji: "🏙️", label: "Ville" },
{ emoji: "🌿", label: "Nature" },
{ emoji: "🌵", label: "Désert" },
{ emoji: "🌴", label: "Tropical" },
{ emoji: "🚤", label: "Bord de lac" },
{ emoji: "🏝️", label: "Île" },
{ emoji: "🏰", label: "Historique" },
{ emoji: "✨", label: "Luxe" },
{ emoji: "🎯", label: "Aventure" },
{ emoji: "🧘", label: "Bien-être" }];


const PARTICIPANT_TIERS = [
  { max: 20, icon: UserRound, label: "Petit comité" },
  { max: 50, icon: UsersRound, label: "Groupe convivial" },
  { max: 100, icon: Users, label: "Grande équipe" },
  { max: 200, icon: Building, label: "Grand événement" },
  { max: Infinity, icon: Landmark, label: "Événement XL" },
];

const getParticipantTier = (count: number) => {
  return PARTICIPANT_TIERS.find((t) => count <= t.max) || PARTICIPANT_TIERS[PARTICIPANT_TIERS.length - 1];
};


const PRICE_PER_PERSON: Record<string, { min: number; max: number }> = {
  seminaire: { min: 150, max: 1000 },
  journee_hors_site: { min: 100, max: 1000 },
  soiree: { min: 50, max: 1000 },
  team_building: { min: 50, max: 1000 },
  conference: { min: 75, max: 1000 },
  lancement_produit: { min: 75, max: 1000 },
  autre: { min: 75, max: 1000 },
};

const STANDING_TIERS = [
  { label: "Essentiel", stars: 1 },
  { label: "Confort", stars: 2 },
  { label: "Premium", stars: 3 },
  { label: "Prestige", stars: 4 },
  { label: "Exception", stars: 5 },
];

const getStandingFromPercent = (percent: number) => {
  if (percent <= 20) return STANDING_TIERS[0];
  if (percent <= 40) return STANDING_TIERS[1];
  if (percent <= 60) return STANDING_TIERS[2];
  if (percent <= 80) return STANDING_TIERS[3];
  return STANDING_TIERS[4];
};


const formatBudgetLabel = (budgetValue: string): string => {
  return budgetValue;
};

const formatEventTypeLabel = (eventValue: string): string => {
  if (eventValue === "autre" || eventValue?.startsWith("autre:")) return eventValue?.replace("autre:", "").trim() || "Autre";
  const found = EVENT_TYPES.find((t) => t.value === eventValue);
  return found ? found.label : eventValue;
};

const FREE_DOMAINS = ["gmail.com","googlemail.com","hotmail.com","hotmail.fr","hotmail.co.uk","hotmail.it","hotmail.es","hotmail.de","hotmail.be","outlook.com","outlook.fr","outlook.es","outlook.it","outlook.de","outlook.be","outlook.co.uk","live.com","live.fr","live.be","live.co.uk","msn.com","yahoo.com","yahoo.fr","yahoo.co.uk","yahoo.es","yahoo.it","yahoo.de","yahoo.ca","ymail.com","rocketmail.com","icloud.com","me.com","mac.com","aol.com","aol.fr","laposte.net","orange.fr","wanadoo.fr","sfr.fr","neuf.fr","club-internet.fr","cegetel.net","numericable.fr","free.fr","aliceadsl.fr","libertysurf.fr","infonie.fr","bbox.fr","tele2.fr","protonmail.com","protonmail.ch","proton.me","pm.me","zoho.com","gmx.fr","gmx.com","gmx.net","gmx.de","mail.com","yandex.com","yandex.fr","yandex.ru","inbox.com","bluewin.ch","t-online.de","web.de","posteo.net","posteo.de","caramail.com","netcourrier.com","keemail.me","tutanota.com","tutanota.de","tuta.io","ueir.jci","voila.fr","noos.fr","skynet.be","telenet.be","proximus.be","belgacom.net","scarlet.be","voo.be","hispeed.ch","sunrise.ch","gmx.ch","swisscom.ch","virgilio.it","libero.it","tiscali.it","tin.it","alice.it","fastwebnet.it","terra.es","telefonica.net","ya.ru","mail.ru","rambler.ru","list.ru","comcast.net","att.net","sbcglobal.net","verizon.net","cox.net","charter.net","earthlink.net","optonline.net","centurylink.net"];

const isProEmail = (email: string) => {
  const domain = email.split("@")[1];
  return domain && !FREE_DOMAINS.includes(domain.toLowerCase());
};

const STEP_IMAGES = [
{ url: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FMRKt1676885471589.jpeg&w=2048&q=75", name: "L'Abbaye de Royaumont", location: "Île-de-France" },
{ url: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2F2FYH1677762931205.jpg&w=2048&q=75", name: "Château d'Ermenonville", location: "Hauts-de-France" },
{ url: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/402466422.jpg", name: "Youza Ecolodge", location: "Île-de-France" },
{ url: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FoxHh1673372697492.jpg&w=2048&q=75", name: "Etiolles Country Club", location: "Île-de-France" },
{ url: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FC9co1677753991331.jpg&w=2048&q=75", name: "Domaine Rocabella", location: "Provence-Alpes-Côte d'Azur" },
{ url: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FfLhq1696321607168.jpg&w=2048&q=75", name: "Hôtel Mercure Chantilly", location: "Hauts-de-France" }];


const getRandomStepImages = () => {
  const shuffled = [...STEP_IMAGES].sort(() => Math.random() - 0.5);
  return shuffled;
};

// Animated counter hook
function useAnimatedCounter(target: number, duration = 600) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    const start = value;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setValue(Math.round(start + (target - start) * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {if (rafRef.current) cancelAnimationFrame(rafRef.current);};
  }, [target, duration]);

  return value;
}

const QuizModal: React.FC<QuizModalProps> = ({ isOpen, onClose }) => {
  const { lang, t } = useArkemaLang();
  const dateLocale = lang === "es" ? es : fr;
  const STEPS_I18N = [
    { label: t("quiz.step.type"), icon: LayoutGrid },
    { label: t("quiz.step.dates"), icon: CalendarDays },
    { label: t("quiz.step.categories"), icon: Compass },
    { label: t("quiz.step.participants"), icon: Users },
    { label: t("quiz.step.standing"), icon: Star },
    { label: t("quiz.step.match"), icon: MapPin },
  ];
  const STEP_TITLES_I18N = [
    t("quiz.title.type"),
    "",
    t("quiz.title.categories"),
    t("quiz.title.participants"),
    t("quiz.title.standing"),
    "",
  ];
  const STEP_LABELS_I18N = [
    t("quiz.label.0"), t("quiz.label.1"), t("quiz.label.2"),
    t("quiz.label.3"), t("quiz.label.4"), t("quiz.label.5"),
  ];
  const MONTHS_I18N = Array.from({ length: 12 }, (_, i) => t(`quiz.month.${i}`));
  const formatEventTypeLabel = (eventValue: string): string => {
    if (eventValue === "autre" || eventValue?.startsWith("autre:")) return eventValue?.replace("autre:", "").trim() || t("quiz.evt.autre");
    if (EVENT_TYPES.find((x) => x.value === eventValue)) return t(`quiz.evt.${eventValue}`);
    return eventValue;
  };

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const [animKey, setAnimKey] = useState(0);
  const [bouncedItem, setBouncedItem] = useState<string | null>(null);
  const [eventType, setEventType] = useState("");
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [categories, setCategories] = useState<string[]>([]);
  const [customParticipants, setCustomParticipants] = useState("");
  const [participantTierKey, setParticipantTierKey] = useState("");
  const [budget, setBudget] = useState("");
  const [sliderBudget, setSliderBudget] = useState(0);
  const [customBudget, setCustomBudget] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepImages] = useState(() => getRandomStepImages());
  const [engagementChoice, setEngagementChoice] = useState<
    "pending" | "email" | "rdv_booking" | "rdv_done"
  >("pending");

  // Reset engagement choice when modal opens/closes
  useEffect(() => {
    setEngagementChoice("pending");
  }, [isOpen]);

  // Listen for HubSpot meeting booking success
  useEffect(() => {
    if (engagementChoice !== "rdv_booking") return;
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { meetingBookSucceeded?: boolean } | undefined;
      if (data && data.meetingBookSucceeded === true) {
        trackPostHogEvent("veolia_meeting_booked", {
          page: "veolia",
          location: "quiz_step6",
          lang,
        });
        setEngagementChoice("rdv_done");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [engagementChoice, lang]);

  const targetPercent = step <= 5 ? COMPATIBILITY_PERCENTS[step] ?? 0 : 100;
  const displayedPercent = useAnimatedCounter(targetPercent);

  const triggerBounce = useCallback((id: string) => {
    setBouncedItem(id);
    setTimeout(() => setBouncedItem(null), 300);
  }, []);

  // Hide HubSpot chat widget while modal is open to prevent z-index conflicts
  useEffect(() => {
    const STYLE_ID = "quiz-modal-hide-hubspot";
    if (isOpen) {
      // Close HubSpot chat panel if it's open, so it doesn't intercept clicks
      try {
        (window as any).HubSpotConversations?.widget?.close?.();
      } catch (_) {}
      if (!document.getElementById(STYLE_ID)) {
        const style = document.createElement("style");
        style.id = STYLE_ID;
        style.textContent =
          "#hubspot-messages-iframe-container, .hs-messages-widget-open, [id^='hubspot-conversations'], .hs-shadow-container { display: none !important; pointer-events: none !important; visibility: hidden !important; }";
        document.head.appendChild(style);
      }
    } else {
      document.getElementById(STYLE_ID)?.remove();
    }
    return () => {
      document.getElementById(STYLE_ID)?.remove();
    };
  }, [isOpen]);

  // Confetti on step 6
  useEffect(() => {
    if (step === 6) {
      const end = Date.now() + 1500;
      const interval = setInterval(() => {
        if (Date.now() > end) {clearInterval(interval);return;}
        confetti({
          particleCount: 30,
          spread: 80,
          origin: { x: Math.random(), y: Math.random() * 0.6 },
          colors: ["#C6E278", "#4CAF50", "#FFD700", "#FF6B6B", "#42A5F5"],
          disableForReducedMotion: true
        });
      }, 200);
      return () => clearInterval(interval);
    }
  }, [step]);


  const toggleMonth = (m: string) =>
  setSelectedMonth((prev) => prev === m ? "" : m);

  const toggleCategory = (c: string) => {
    setCategories((prev) => {
      if (prev.includes(c)) return prev.filter((x) => x !== c);
      if (prev.length >= 4) return prev;
      return [...prev, c];
    });
    triggerBounce(`cat-${c}`);
  };

  const validateEmail = () => {
    if (!email) {setEmailError(t("quiz.email.required"));return false;}
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {setEmailError(t("quiz.email.invalid"));return false;}
    if (!isProEmail(email)) {setEmailError(t("quiz.email.pro"));return false;}
    setEmailError("");
    return true;
  };

  const canProceed = () => {
    switch (step) {
      case 0:return !!eventType;
      case 1:return !!selectedMonth || !!dateRange?.from;
      case 2:return categories.length > 0;
      case 3:return !!customParticipants && parseInt(customParticipants) > 0;
      case 4:return sliderBudget > 0 || !!customBudget;
      default:return true;
    }
  };

  const handleNext = () => {
    if (step < STEPS_I18N.length - 1) {
      setDirection("forward");
      setAnimKey((k) => k + 1);
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setDirection("backward");
      setAnimKey((k) => k + 1);
      setStep((s) => s - 1);
    }
  };

  const handleStepClick = (i: number) => {
    if (i < step && step <= 5) {
      setDirection("backward");
      setAnimKey((k) => k + 1);
      setStep(i);
    }
  };

  const handleClose = () => {
    setStep(0);setEventType("");setDateRange(undefined);setSelectedMonth("");
    setCategories([]);setCustomParticipants("");setParticipantTierKey("");setBudget("");setSliderBudget(0);setCustomBudget("");setEmail("");setEmailError("");
    setIsSubmitting(false);
    setIsSubmitting(false);
    setEngagementChoice("pending");
    onClose();
  };

  const handleSubmitQuiz = async () => {
    if (!validateEmail()) return;
    setIsSubmitting(true);

    try {
      let dateDisplay = "";
      if (selectedMonth) {
        dateDisplay = selectedMonth;
      } else if (dateRange?.from && dateRange?.to) {
        dateDisplay = `${format(dateRange.from, "d MMM yyyy", { locale: dateLocale })} – ${format(dateRange.to, "d MMM yyyy", { locale: dateLocale })}`;
      } else if (dateRange?.from) {
        dateDisplay = format(dateRange.from, "d MMM yyyy", { locale: dateLocale });
      } else {
        dateDisplay = lang === "es" ? "No especificado" : "Non spécifié";
      }

      const storedUTMParams = getStoredUTMParams();
      const urlParams = new URLSearchParams(window.location.search);
      const campaignName = getCampaignName(urlParams, storedUTMParams) || storedUTMParams.utm_campaign || '';
      const utmTerm = urlParams.get('utm_term') || storedUTMParams.utm_term || '';
      const fullUrl = getInitialUrl();

      const participantsDisplay = customParticipants;
      const pCount = parseInt(customParticipants) || 1;
      const budgetDisplay = customBudget ? `${customBudget}€` : `${sliderBudget.toLocaleString("fr-FR")}€ (${Math.round(sliderBudget / pCount).toLocaleString("fr-FR")}€/pers)`;
      const deviceType = window.innerWidth < 768 ? "Mobile" : "Ordinateur";
      const slackMessage = `*Nouvelle demande (Quiz Destinations)*\n*Canal d'acquisition* : Quiz Destinations\n*Market* : FR\n\n*Source du lead* : Landing Lovable – Quiz\n*E-Mail professionnel* : ${email}\n*Type d'événement* : ${formatEventTypeLabel(eventType)}\n*Dates* : ${dateDisplay}\n*Catégories* : ${categories.join(", ")}\n*Participants* : ${participantsDisplay}\n*Budget* : ${budgetDisplay}\n\n*UTM Parameters* : Campagne: ${campaignName} | Mot-clé: ${decodeURIComponent(utmTerm)}\n*Link*: ${fullUrl.replace('https://', 'https ://')}\n*Support* : ${deviceType}`;

      const slackChannel = email === "test@naboo.app" ? "C09TA10FMU5" : "C0BSWUS2DNX";
      await sendSlackMessage(slackMessage, slackChannel);

      posthog.capture('quiz_modal_completed');

      setStep(6);
    } catch (error) {
      console.error("Quiz submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const slideClass = direction === "forward" ? "animate-slide-in-right" : "animate-slide-in-left";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className={cn(
          "p-0 gap-0 overflow-hidden",
          step === 6 && engagementChoice === "rdv_booking"
            ? "max-w-4xl w-[95vw] h-[95vh] sm:h-[760px]"
            : "max-w-5xl h-[90vh] sm:h-[620px]"
        )}
      >
        <div className="flex flex-row h-full">
          {/* Left image — hidden on mobile and success step */}
          {step <= 5 &&
          <div className="hidden sm:block sm:w-[35%] relative flex-shrink-0 h-full">
              <img
              src={stepImages[step % stepImages.length].url}
              alt={stepImages[step % stepImages.length].name}
              className="w-full h-full object-cover transition-opacity duration-500" />
            
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-5 pt-14 text-center">
                <p className="text-white font-medium text-base">{stepImages[step % stepImages.length].name}</p>
                <p className="text-white/80 text-sm flex items-center justify-center gap-1 mt-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {stepImages[step % stepImages.length].location}
                </p>
              </div>
            </div>
          }

          {/* Right content */}
          <div className={cn("flex flex-col flex-1 h-full overflow-hidden", step <= 5 ? "sm:w-[65%]" : "w-full")}>
            {/* Header - hidden on success step */}
            {step <= 5 &&
            <div className="px-8 pt-5 pb-3 space-y-3 relative shrink-0">
                <button
                onClick={handleClose}
                className="absolute top-3 right-3 p-1.5 rounded-full text-foreground hover:bg-muted transition-colors z-10"
                aria-label={t("quiz.aria.close")}>
                  <X className="h-4 w-4" />
                </button>

                {/* Stepper with badges — at the very top */}
                <div className="flex items-center gap-1.5 text-muted-foreground overflow-x-auto text-sm pr-8">
                  {STEPS_I18N.map((s, i) => {
                  const Icon = s.icon;
                  const isCompleted = i < step;
                  const isCurrent = i === step;
                  const isClickable = isCompleted && step <= 5;
                  return (
                    <React.Fragment key={s.label}>
                        {i > 0 && <span className="text-border">›</span>}
                        <span
                        className={cn("flex items-center gap-1 whitespace-nowrap transition-colors text-xs",
                        isCurrent ? "text-cta-green font-semibold" :
                        isCompleted ? "text-foreground cursor-pointer hover:text-cta-green" : ""
                        )}
                        onClick={isClickable ? () => handleStepClick(i) : undefined}
                        role={isClickable ? "button" : undefined}>
                        
                          {isCompleted ?
                        <CheckCircle className="h-3 w-3 text-cta-green" /> :

                        <Icon className="h-3 w-3" />
                        }
                          <span className="hidden sm:inline">{s.label}</span>
                        </span>
                      </React.Fragment>);

                })}
                </div>

                {/* Compatibility counter — right below stepper */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                    className="h-full bg-cta-green rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${displayedPercent}%` }} />
                  </div>
                  <span className="font-semibold text-cta-green whitespace-nowrap text-sm">
                    {displayedPercent}{t("quiz.match_suffix")}
                  </span>
                </div>

                {/* Step title */}
                <DialogTitle className="text-base font-semibold text-foreground">
                  {step === 1 ? `${t("quiz.title.dates_prefix")}${eventType === "autre" || eventType?.startsWith("autre:") || !eventType ? t("quiz.title.event_word") : ((lang === "es" || lang === "de") ? formatEventTypeLabel(eventType) : formatEventTypeLabel(eventType).toLowerCase())}${t("quiz.title.dates_suffix")}` : STEP_TITLES_I18N[step]}
                </DialogTitle>

                {/* Step label moved to navigation bar */}
              </div>
            }

            {/* Content */}
            <div className="px-8 pb-6 h-full min-h-0 flex flex-col overflow-y-auto">

              {/* Step 0: Event type */}
              {step === 0 &&
              <div key={`step0-${animKey}`} className={cn("flex-1 flex flex-col", slideClass)}>
                  <div className="grid grid-cols-2 gap-3 auto-rows-fr">
                    {EVENT_TYPES.map((et, idx) => (
                      <button key={et.value} type="button"
                      onClick={() => {setEventType(et.value);triggerBounce(`evt-${et.value}`);}}
                      style={{ animationDelay: `${idx * 50}ms` }}
                      className={cn(
                        "flex items-center justify-center rounded-lg border px-3 py-3 text-sm transition-colors animate-stagger-in",
                        eventType === et.value ? "border-cta-green bg-cta-green/10 text-foreground font-medium" : "border-border text-foreground hover:border-muted-foreground/30",
                        bouncedItem === `evt-${et.value}` && "animate-bounce-select"
                      )}>
                          {t(`quiz.evt.${et.value}`)}
                        </button>
                    ))}
                    {eventType !== "autre" && !eventType?.startsWith("autre:") ? (
                      <button type="button"
                        onClick={() => {setEventType("autre:");triggerBounce("evt-autre");}}
                        style={{ animationDelay: `${EVENT_TYPES.length * 50}ms` }}
                        className={cn(
                          "flex items-center justify-center rounded-lg border px-3 py-3 text-sm transition-colors animate-stagger-in",
                          "border-border text-foreground hover:border-muted-foreground/30",
                          bouncedItem === "evt-autre" && "animate-bounce-select"
                        )}>
                          {t("quiz.evt.autre")}
                        </button>
                    ) : eventType === "autre" || eventType?.startsWith("autre:") ? (
                      <div
                        style={{ animationDelay: `${EVENT_TYPES.length * 50}ms` }}
                        className="rounded-lg border border-cta-green bg-cta-green/10 px-3 py-1.5 text-sm animate-stagger-in flex items-center">
                        <Input
                          autoFocus
                          placeholder={t("quiz.evt.placeholder")}
                          value={eventType === "autre:" ? "" : (eventType?.replace("autre:", "") || "")}
                          onChange={(e) => setEventType(`autre:${e.target.value}`)}
                          className="border-0 bg-transparent p-0 h-auto text-sm focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/60"
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              }

              {/* Step 1: Dates */}
              {step === 1 &&
              <div key={`step1-${animKey}`} className={cn("space-y-4 flex-1", slideClass)}>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !dateRange?.from && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange?.from ?
                      dateRange.to ?
                      `${format(dateRange.from, "d MMM yyyy", { locale: dateLocale })} – ${format(dateRange.to, "d MMM yyyy", { locale: dateLocale })}` :
                      format(dateRange.from, "d MMM yyyy", { locale: dateLocale }) :
                      t("quiz.dates.placeholder")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="range" selected={dateRange} onSelect={setDateRange}
                    numberOfMonths={1} className="p-3 pointer-events-auto" />
                    </PopoverContent>
                  </Popover>
                  <div>
                    <p className="text-xs text-foreground mb-2">{t("quiz.dates.or_month")}</p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 auto-rows-fr">
                      {MONTHS.map((m, idx) =>
                    <button key={m} type="button" onClick={() => {toggleMonth(m);triggerBounce(`month-${m}`);}}
                    style={{ animationDelay: `${idx * 40}ms` }}
                    className={cn(
                      "flex items-center justify-center rounded-lg border px-3 py-3 text-sm transition-colors animate-stagger-in",
                      selectedMonth === m ? "border-cta-green bg-cta-green/10 text-foreground" : "border-border text-foreground hover:border-muted-foreground/30",
                      bouncedItem === `month-${m}` && "animate-bounce-select"
                    )}>
                          {MONTHS_I18N[idx]}
                        </button>
                    )}
                    </div>
                  </div>
                </div>
              }

              {/* Step 2: Categories */}
              {step === 2 &&
              <div key={`step2-${animKey}`} className={cn("space-y-3 flex-1", slideClass)}>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 auto-rows-fr">
                    {CATEGORIES.map((c, idx) =>
                  <button key={c.label} type="button" onClick={() => toggleCategory(c.label)}
                  style={{ animationDelay: `${idx * 50}ms` }}
                  className={cn(
                    "flex items-center justify-center gap-2 rounded-lg border px-3 py-3 text-sm transition-colors animate-stagger-in",
                    categories.includes(c.label) ? "border-cta-green bg-cta-green/10 text-foreground font-medium" : "border-border text-foreground hover:border-muted-foreground/30",
                    categories.length >= 4 && !categories.includes(c.label) && "opacity-40 cursor-not-allowed",
                    bouncedItem === `cat-${c.label}` && "animate-bounce-select"
                  )}>
                        <span className="text-base">{c.emoji}</span>
                        {t(`quiz.cat.${c.label}`)}
                      </button>
                  )}
                  </div>
                </div>
              }

              {/* Step 3: Participants — Free input gamifié */}
              {step === 3 &&
              <div key={`step3-${animKey}`} className={cn("flex-1 flex flex-col items-center justify-center gap-5", slideClass)}>
                  {(() => {
                    const count = parseInt(customParticipants) || 0;
                    const tier = count > 0 ? getParticipantTier(count) : null;
                    const prevTierKey = participantTierKey;
                    const currentTierKey = tier ? tier.label : "";
                    const shouldBounce = currentTierKey && currentTierKey !== prevTierKey;

                    if (shouldBounce && currentTierKey !== participantTierKey) {
                      setTimeout(() => setParticipantTierKey(currentTierKey), 0);
                    }

                    const currentTierIndex = tier ? PARTICIPANT_TIERS.indexOf(tier) : -1;

                    const handleIncrement = () => {
                      setCustomParticipants(prev => {
                        const current = parseInt(prev) || 0;
                        const step = current < 20 ? 1 : 5;
                        return String(Math.min(current + step, 10000));
                      });
                    };

                    const handleDecrement = () => {
                      setCustomParticipants(prev => {
                        const current = parseInt(prev) || 0;
                        if (current <= 1) return prev;
                        const step = current <= 20 ? 1 : 5;
                        return String(Math.max(current - step, 1));
                      });
                    };

                    return (
                      <>
                        {/* Dynamic tier icon */}
                        <div className="flex flex-col items-center gap-2 min-h-[80px] justify-center">
                          {tier ? (
                            <div key={currentTierKey} className="flex flex-col items-center gap-1.5 animate-in zoom-in-75 duration-300">
                              <tier.icon className="h-12 w-12 text-cta-green" />
                              <span className="text-sm font-semibold text-foreground">{t(`quiz.tier.${tier.label}`)}</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
                              <Users className="h-12 w-12" />
                              <span className="text-sm">{t("quiz.participants.hint")}</span>
                            </div>
                          )}
                        </div>

                        {/* Counter with +/- buttons */}
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={handleDecrement}
                            onMouseDown={() => {
                              const id = setInterval(() => handleDecrement(), 120);
                              const acc = setTimeout(() => { clearInterval(id); (window as any).__fastInt = setInterval(() => handleDecrement(), 50); }, 1000);
                              (window as any).__holdInt = id; (window as any).__holdTo = acc;
                            }}
                            onMouseUp={() => { clearInterval((window as any).__holdInt); clearInterval((window as any).__fastInt); clearTimeout((window as any).__holdTo); }}
                            onMouseLeave={() => { clearInterval((window as any).__holdInt); clearInterval((window as any).__fastInt); clearTimeout((window as any).__holdTo); }}
                            onTouchStart={() => {
                              const id = setInterval(() => handleDecrement(), 120);
                              const acc = setTimeout(() => { clearInterval(id); (window as any).__fastInt = setInterval(() => handleDecrement(), 50); }, 1000);
                              (window as any).__holdInt = id; (window as any).__holdTo = acc;
                            }}
                            onTouchEnd={() => { clearInterval((window as any).__holdInt); clearInterval((window as any).__fastInt); clearTimeout((window as any).__holdTo); }}
                            disabled={!customParticipants || parseInt(customParticipants) <= 1}
                            className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-muted/60 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <Input
                            type="number"
                            style={{ MozAppearance: 'textfield' }}
                            placeholder={t("quiz.participants.placeholder")}
                            min={1}
                            max={10000}
                            autoFocus
                            value={customParticipants}
                            onChange={(e) => setCustomParticipants(e.target.value)}
                            className="text-center text-2xl font-semibold h-14 w-[140px] rounded-xl border-2 focus-visible:border-cta-green [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          />
                          <button
                            type="button"
                            onClick={handleIncrement}
                            onMouseDown={() => {
                              const id = setInterval(() => handleIncrement(), 120);
                              const acc = setTimeout(() => { clearInterval(id); (window as any).__fastInt = setInterval(() => handleIncrement(), 50); }, 1000);
                              (window as any).__holdInt = id; (window as any).__holdTo = acc;
                            }}
                            onMouseUp={() => { clearInterval((window as any).__holdInt); clearInterval((window as any).__fastInt); clearTimeout((window as any).__holdTo); }}
                            onMouseLeave={() => { clearInterval((window as any).__holdInt); clearInterval((window as any).__fastInt); clearTimeout((window as any).__holdTo); }}
                            onTouchStart={() => {
                              const id = setInterval(() => handleIncrement(), 120);
                              const acc = setTimeout(() => { clearInterval(id); (window as any).__fastInt = setInterval(() => handleIncrement(), 50); }, 1000);
                              (window as any).__holdInt = id; (window as any).__holdTo = acc;
                            }}
                            onTouchEnd={() => { clearInterval((window as any).__holdInt); clearInterval((window as any).__fastInt); clearTimeout((window as any).__holdTo); }}
                            className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-muted/60 transition-colors"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Amphitheater avatar grid */}
                        {(() => {
                          const count = parseInt(customParticipants) || 0;
                          const ROWS = [6, 8, 10, 12, 12]; // total 48
                          const TOTAL_ICONS = 48;
                          const maxRef = 200;
                          const filledCount = Math.min(Math.round((count / maxRef) * TOTAL_ICONS), TOTAL_ICONS);
                          const rowSizes: string[] = ["h-6 w-6", "h-[22px] w-[22px]", "h-5 w-5", "h-[18px] w-[18px]", "h-4 w-4"];
                          const rowPaddings: string[] = ["px-10", "px-7", "px-4", "px-1", "px-0"];
                          let globalIdx = 0;
                          return (
                            <div className="w-full max-w-[380px] mt-4 space-y-1.5">
                              <style>{`
                                @keyframes subtle-bounce {
                                  0%, 100% { transform: translateY(0); }
                                  50% { transform: translateY(-2px); }
                                }
                              `}</style>
                              {ROWS.map((rowCount, rowIdx) => {
                                const icons = Array.from({ length: rowCount }).map((_, colIdx) => {
                                  const idx = globalIdx++;
                                  const isFilled = idx < filledCount;
                                  const bounceDelay = ((idx * 7 + 3) % 20) * 0.8;
                                  return (
                                    <UserRound
                                      key={idx}
                                      className={cn(
                                        rowSizes[rowIdx],
                                        "transition-all duration-300",
                                        isFilled
                                          ? "text-cta-green scale-100 opacity-100"
                                          : "text-muted-foreground/20 scale-90 opacity-60"
                                      )}
                                      style={isFilled ? {
                                        animation: `subtle-bounce 3s ease-in-out infinite`,
                                        animationDelay: `${bounceDelay}s`,
                                        transitionDelay: `${idx * 15}ms`,
                                      } : {
                                        transitionDelay: `${idx * 10}ms`,
                                      }}
                                    />
                                  );
                                });
                                return (
                                  <div key={rowIdx} className={cn("flex justify-center gap-1.5", rowPaddings[rowIdx])}>
                                    {icons}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()}
                </div>
              }

              {/* Step 4: Budget — Dynamic Slider */}
              {step === 4 &&
              <div key={`step4-${animKey}`} className={cn("flex-1 flex flex-col justify-center", slideClass)}>
                  {(() => {
                    const tier = PRICE_PER_PERSON[eventType] || (eventType?.startsWith("autre:") ? PRICE_PER_PERSON.autre : PRICE_PER_PERSON.autre);
                    const minPP = tier.min;
                    const maxPP = tier.max;
                    const pCount = parseInt(customParticipants) || 1;
                    const minTotal = minPP * pCount;
                    const maxTotal = maxPP * pCount;
                    const activeBudget = (budget === "custom" && customBudget) ? parseInt(customBudget.replace(/\s/g, "")) || 0 : 0;
                    const currentTotal = activeBudget > 0 ? activeBudget : (sliderBudget || Math.round((minTotal + maxTotal) / 2));
                    const pricePerPerson = Math.round(currentTotal / pCount);
                    const clampedTotal = Math.min(Math.max(currentTotal, minTotal), maxTotal);
                    const percent = maxTotal > minTotal ? ((clampedTotal - minTotal) / (maxTotal - minTotal)) * 100 : 50;
                    const standing = getStandingFromPercent(percent);

                    // Initialize slider on first render of this step
                    if (sliderBudget === 0 && !customBudget) {
                      const defaultVal = Math.round((minTotal + maxTotal) / 2);
                      setTimeout(() => setSliderBudget(defaultVal), 0);
                    }

                    const sliderStep = (() => { const range = maxTotal - minTotal; return range <= 5000 ? 100 : range <= 20000 ? 500 : 1000; })();

                    return (
                      <div className="flex flex-col gap-8">
                        {/* Standing label + stars */}
                        <div className="flex flex-col items-center gap-1 animate-in fade-in-0 duration-300">
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: standing.stars }).map((_, i) => (
                              <Star key={i} className="h-4 w-4 text-amber-500 fill-amber-500" />
                            ))}
                            {Array.from({ length: 5 - standing.stars }).map((_, i) => (
                              <Star key={`empty-${i}`} className="h-4 w-4 text-muted-foreground/30" />
                            ))}
                          </div>
                          <span className="text-sm font-medium text-foreground">{t(`quiz.standing.${standing.label}`)}</span>
                        </div>

                        {/* Budget amount display — total */}
                        <div className="text-center">
                          <span className="text-3xl font-bold text-foreground transition-all duration-200">
                            {(budget === "custom" && customBudget) ? `${customBudget} €` : `${currentTotal.toLocaleString("fr-FR")} €${clampedTotal >= maxTotal ? '+' : ''}`}
                          </span>
                          <p className="text-sm text-muted-foreground mt-1">
                            {t("quiz.budget.per_person").replace("{p}", pricePerPerson.toLocaleString("fr-FR"))}
                          </p>
                        </div>

                        {/* Slider — always visible */}
                        <div className={cn("px-1", budget === "custom" && customBudget && "opacity-40 transition-opacity duration-200")}>
                          <Slider
                            value={[Math.min(Math.max(activeBudget > 0 ? activeBudget : (sliderBudget || clampedTotal), minTotal), maxTotal)]}
                            min={minTotal}
                            max={maxTotal}
                            step={sliderStep}
                            onValueChange={(vals) => {
                              setSliderBudget(vals[0]);
                              setBudget("");
                              setCustomBudget("");
                            }}
                            className="w-full"
                          />
                            <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
                              <span>{minTotal.toLocaleString("fr-FR")} €</span>
                              <span>{maxTotal.toLocaleString("fr-FR")} €+</span>
                            </div>
                          </div>

                        {/* OU separator */}
                        <div className="flex items-center gap-3 self-center w-full max-w-[200px]">
                          <div className="h-px flex-1 bg-border" />
                          <span className="text-xs text-muted-foreground font-medium">{t("quiz.budget.or")}</span>
                          <div className="h-px flex-1 bg-border" />
                        </div>

                        {/* Custom budget input — total amount */}
                        <button type="button"
                          onClick={() => { if (budget === "custom") { setBudget(""); setCustomBudget(""); } else { setBudget("custom"); triggerBounce("budget-custom"); } }}
                          className={cn(
                            "inline-flex items-center self-center rounded-lg border px-3 py-2.5 text-sm transition-colors w-auto",
                            budget === "custom" ? "border-cta-green bg-cta-green/10 text-foreground font-medium" : "border-border text-foreground hover:border-muted-foreground/30"
                          )}>
                          {budget === "custom" ? (
                            <div className="flex items-center gap-1.5">
                              <Input
                                type="text"
                                placeholder={t("quiz.budget.placeholder")}
                                autoFocus
                                value={customBudget}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => { const raw = e.target.value.replace(/\s/g, "").replace(/[^0-9]/g, ""); setCustomBudget(raw ? parseInt(raw).toLocaleString("fr-FR").replace(/\u202F/g, " ") : ""); }}
                                className="border-0 bg-transparent text-center text-sm p-0 h-auto w-24 focus-visible:ring-0 placeholder:text-muted-foreground"
                              />
                              <Euro className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            </div>
                          ) : (
                            <span className="flex items-center gap-1.5">
                              {t("quiz.budget.exact")}
                              <Euro className="h-3.5 w-3.5 text-muted-foreground" />
                            </span>
                          )}
                        </button>
                      </div>
                    );
                  })()}
                </div>
              }

              {/* Step 5: Votre match (email) */}
              {step === 5 &&
              <div key={`step5-${animKey}`} className={cn("flex-1 flex flex-col items-center justify-center text-center space-y-6 py-6", slideClass)}>
                  <div className="h-14 w-14 rounded-full bg-foreground/5 flex items-center justify-center">
                    <Lock className="h-6 w-6 text-foreground" strokeWidth={1.5} />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold text-foreground">{t("quiz.match.title")}</h3>
                    <p className="text-sm text-foreground max-w-sm mx-auto">{t("quiz.match.subtitle")}</p>
                  </div>
                  <div className="w-full max-w-sm space-y-1.5">
                    <Input type="email" placeholder={t("quiz.match.email_placeholder")} value={email}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEmail(val);
                    if (emailError) setEmailError("");
                    if (val && val.includes("@") && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) && !isProEmail(val)) {
                      setEmailError(t("quiz.email.pro"));
                    }
                  }} />
                    {emailError && <p className="text-xs text-destructive">{emailError}</p>}
                  </div>
                  <Button className="bg-cta-green hover:bg-cta-green/90 text-cta-green-foreground rounded-xl px-8 py-3 h-auto text-base"
                disabled={!email || isSubmitting}
                onClick={handleSubmitQuiz}>
                    {isSubmitting ? t("quiz.match.sending") : t("quiz.match.cta")}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1 text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-4 w-4" /> {t("quiz.nav.back")}
                  </Button>
                </div>
              }

              {/* Step 6: Engagement choice / confirmation */}
              {step === 6 && engagementChoice === "pending" &&
              <div className="flex-1 flex flex-col justify-center py-4 px-2 sm:px-6 animate-stagger-in overflow-y-auto">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground text-center mb-2">{t("quiz.engagement.subtitle")}</p>
                  <h3 className="text-xl md:text-2xl font-medium text-foreground text-center mb-6">{t("quiz.engagement.title")}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Email card */}
                    <div className="flex flex-col rounded-2xl border border-border bg-card p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center"><Mail className="h-5 w-5 text-foreground" /></div>
                        <h4 className="text-base font-medium text-foreground">{t("quiz.engagement.email_title")}</h4>
                      </div>
                      <p className="text-xs italic text-muted-foreground mb-3">{t("quiz.engagement.email_desc")}</p>
                      <ul className="space-y-1.5 mb-4 flex-1">
                        {["email_b1","email_b2","email_b3"].map(k => (
                          <li key={k} className="flex items-start gap-2 text-xs text-foreground">
                            <CheckCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-cta-green" />
                            <span>{t(`quiz.engagement.${k}`)}</span>
                          </li>
                        ))}
                      </ul>
                      <Button className="w-full bg-cta-green hover:bg-cta-green/90 text-cta-green-foreground rounded-xl"
                        onClick={() => {
                          trackPostHogEvent("veolia_quiz_engagement_choice", { page: "veolia", choice: "email", lang });
                          setEngagementChoice("email");
                        }}>
                        {t("quiz.engagement.email_cta")}
                      </Button>
                    </div>
                    {/* RDV card */}
                    <div className="flex flex-col rounded-2xl border-2 border-cta-green bg-card p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center"><CalendarIcon className="h-5 w-5 text-foreground" /></div>
                        <h4 className="text-base font-medium text-foreground">{t("quiz.engagement.rdv_title")}</h4>
                      </div>
                      <p className="text-xs italic text-muted-foreground mb-3">{t("quiz.engagement.rdv_desc")}</p>
                      <ul className="space-y-1.5 mb-4 flex-1">
                        {["rdv_b1","rdv_b2","rdv_b3"].map(k => (
                          <li key={k} className="flex items-start gap-2 text-xs text-foreground">
                            <CheckCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-cta-green" />
                            <span>{t(`quiz.engagement.${k}`)}</span>
                          </li>
                        ))}
                      </ul>
                      <Button className="w-full bg-cta-green hover:bg-cta-green/90 text-cta-green-foreground rounded-xl"
                        onClick={() => {
                          trackPostHogEvent("veolia_quiz_engagement_choice", { page: "veolia", choice: "rdv_booking", lang });
                          setEngagementChoice("rdv_booking");
                        }}>
                        {t("quiz.engagement.rdv_cta")}
                      </Button>
                    </div>
                  </div>
                </div>
              }

              {step === 6 && engagementChoice === "email" &&
              <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4 py-8 animate-stagger-in">
                  <div className="h-14 w-14 rounded-full bg-cta-green/20 flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-cta-green" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">{t("quiz.success.title")}</h3>
                  <p className="text-sm text-foreground max-w-sm">{t("quiz.success.subtitle")}</p>
                  <Button className="bg-cta-green hover:bg-cta-green/90 text-cta-green-foreground rounded-xl" onClick={handleClose}>
                    {t("quiz.success.close")}
                  </Button>
                </div>
              }

              {step === 6 && engagementChoice === "rdv_booking" &&
              <div className="flex-1 flex flex-col animate-stagger-in overflow-hidden relative">
                  <button
                    type="button"
                    onClick={() => setEngagementChoice("pending")}
                    className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground bg-white/80 backdrop-blur rounded-md"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> {t("quiz.nav.back")}
                  </button>
                  <iframe
                    src={getCeliaMeetingEmbedUrl(lang)}
                    title={t("quiz.engagement.rdv_modal_title")}
                    className="w-full h-full flex-1 border-0"
                  />
                </div>
              }

              {step === 6 && engagementChoice === "rdv_done" &&
              <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4 py-8 animate-stagger-in">
                  <div className="h-14 w-14 rounded-full bg-cta-green/20 flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-cta-green" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">{t("quiz.engagement.rdv_success_title")}</h3>
                  <p className="text-sm text-foreground max-w-sm">{t("quiz.engagement.rdv_success_subtitle")}</p>
                  <Button className="bg-cta-green hover:bg-cta-green/90 text-cta-green-foreground rounded-xl" onClick={handleClose}>
                    {t("quiz.success.close")}
                  </Button>
                </div>
              }

              {/* Navigation */}
              {step < 5 &&
              <div className="flex items-center justify-between pt-6 mt-auto">
                  {step > 0 ?
                <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1 text-foreground">
                      <ArrowLeft className="h-4 w-4" /> {t("quiz.nav.back")}
                    </Button> :
                <span />}
                  <p className="text-xs text-muted-foreground text-center flex-1">{STEP_LABELS_I18N[step]}</p>
                  <Button size="sm" onClick={handleNext} disabled={!canProceed()}
                className="gap-1 bg-cta-green hover:bg-cta-green/90 text-cta-green-foreground rounded-xl">
                    {t("quiz.nav.next")} <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              }
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>);

};

export default QuizModal;